import { TpAreaRenderContext } from '../zoompan-canvas/zoomPanCanvasHelpers';
import { Color } from './colors';
import { calcMax, calcMin, calcAverage, calcQuantile } from './statistics';

export const KERNEL_DISPLAY_TYPE = {
  DENSITY: 'density',
  DENSITY_BOX: 'densityBox',
  BOX_MIN_MAX_WHISKERS: 'boxMinMaxWhiskers',
  MEAN: 'mean',
  MEAN_MIN_MAX_WHISKERS: 'meanMinMaxWhiskers',
};

export interface TpKernelDFData {
  hasData: boolean;
  densityStart: number;
  densityStep: number;
  densityValues: number[]; //enumerate densities on an equidistant grid, starting at densityStart, with intervals densityStep
  maxDensity: number;
  minValue: number;
  maxValue: number;
  meanValue: number;
  q25: number; // 25 percentile
  q50: number; // median
  q75: number; // 75 percentile
  stDev: number; // Standard deviation of the values
  se: number; // Standard error of the mean
}

export function createEmptyKernelDF(): TpKernelDFData {
  return {
    hasData: false,
    densityStart: 0,
    densityStep: 1,
    densityValues: [],
    maxDensity: 0,
    minValue: 0,
    maxValue: 0,
    meanValue: 0,
    q25: 0,
    q50: 0,
    q75: 0,
    stDev: 0,
    se: 0,
  };
}

/**
 * Draw marker for the mean value of the distribution.
 *
 * Current implementation draws a cross, but can be easily changed to other marker types if desired (e.g. circle, square, diamond).
 * @param ctx - The CanvasRenderingContext2D
 * @param x - Center X coordinate
 * @param y - Center Y coordinate
 * @param size - The size of the marker.
 * @param color - Color of the marker
 * @param lineWidth - Thickness of the lines
 */
function drawMeanMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  lineWidth: number = 2,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  // Use a round cap so the edges of the cross aren't awkwardly squared off
  ctx.lineCap = 'round';

  // Draw the top-left to bottom-right line (\)
  ctx.moveTo(x - size, y - size);
  ctx.lineTo(x + size, y + size);

  // Draw the bottom-left to top-right line (/)
  ctx.moveTo(x - size, y + size);
  ctx.lineTo(x + size, y - size);

  ctx.stroke();
  ctx.restore();
}

export function calculateKernelDF(values: number[], kernelSize: number, gammaFactor: number): TpKernelDFData {
  if (values.length == 0) return createEmptyKernelDF(); // nothing to compute
  const pointCount = 200; //number of sampling points for the density function, chosen sufficiently high to create a smooth looking function
  const minValue = calcMin(values);
  const maxValue = calcMax(values);
  const meanValue = calcAverage(values);
  const stDev = Math.sqrt(values.reduce((sum, val) => sum + (val - meanValue) ** 2, 0) / values.length);
  const se = stDev / Math.sqrt(values.length);
  if (maxValue < minValue) return createEmptyKernelDF(); // nothing to compute

  const densityStart = minValue - 4 * kernelSize;
  const densityStep = (maxValue - minValue + 8 * kernelSize) / (pointCount - 1);

  // Calculate a binned histogram first
  const binCounts = new Array(pointCount).fill(0);
  for (const value of values) {
    const binIdx = Math.round((value - densityStart) / densityStep);
    if (binIdx < 0 || binIdx >= pointCount) throw `Invalid bin ${binIdx} | value=${value}`; // this should never happen
    binCounts[binIdx]++;
  }

  // Calculate densities using a kernel function
  let densities: number[] = new Array(pointCount).fill(0);
  for (let pointIdx1 = 0; pointIdx1 < pointCount; pointIdx1++) {
    if (binCounts[pointIdx1] > 0) {
      for (let pointIdx2 = 0; pointIdx2 < pointCount; pointIdx2++) {
        const df = (pointIdx2 - pointIdx1) * densityStep;
        const kernelFactor = Math.exp(-((df / kernelSize) ** 2));
        densities[pointIdx2] += binCounts[pointIdx1] * kernelFactor;
      }
    }
  }
  densities = densities.map((val) => val ** gammaFactor);

  const df = {
    hasData: true,
    densityStart,
    densityStep,
    densityValues: densities,
    maxDensity: Math.max(...densities),
    minValue,
    maxValue,
    meanValue,
    q25: calcQuantile(values, 0.25),
    q50: calcQuantile(values, 0.5),
    q75: calcQuantile(values, 0.75),
    stDev,
    se,
  };
  return df;
}

/**
 * Canvas context needed for rendering the kernel density function
 */
export interface TpRenderKernelContext {
  canvasContext: CanvasRenderingContext2D;
  mapx: (x: number) => number;
  mapy: (y: number) => number;
  pixelConv: (size: number) => number;
}

/**
 * Renders the kernel density function on the canvas as a violin-plot-like shape.
 */
function renderKernelDFDensity(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  df: TpKernelDFData,
  maxDensity: number,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
) {
  const { mapx, mapy } = renderContext;
  const ctx = renderContext.canvasContext;
  // show density
  ctx.beginPath();
  for (let pointIdx1 = 0; pointIdx1 < df.densityValues.length; pointIdx1++) {
    const x = mapx(centerX + ((df.densityValues[pointIdx1] / maxDensity) * sizeX) / 2);
    const y = mapy(df.densityStart + pointIdx1 * df.densityStep);
    if (pointIdx1 === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  for (let pointIdx1 = df.densityValues.length - 1; pointIdx1 >= 0; pointIdx1--) {
    const x = mapx(centerX - ((df.densityValues[pointIdx1] / maxDensity) * sizeX) / 2);
    const y = mapy(df.densityStart + pointIdx1 * df.densityStep);
    ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = fillColor.toCanvasString();
  ctx.strokeStyle = outlineColor.toCanvasString();
  ctx.fill();
  ctx.stroke();
}

/**
 * Renders the kernel density function on the canvas as a violin-plot-like shape with an extra box overlay.
 */
function renderKernelDFDensityBox(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  df: TpKernelDFData,
  maxDensity: number,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
) {
  const { mapx, mapy, pixelConv } = renderContext;
  const ctx = renderContext.canvasContext;
  renderKernelDFDensity(renderContext, df, maxDensity, outlineColor, fillColor, centerX, sizeX);
  // show box superimposed on density
  ctx.fillStyle = outlineColor.toCanvasString();
  ctx.fillRect(mapx(centerX) - pixelConv(1.5), mapy(df.q75), pixelConv(3), mapy(df.q25) - mapy(df.q75));
  ctx.fillRect(
    mapx(centerX - 0.45 * sizeX),
    mapy(df.q50) - pixelConv(0.5),
    mapx(centerX + 0.45 * sizeX) - mapx(centerX - 0.45 * sizeX),
    pixelConv(1),
  );
}

/**
 * Renders the kernel density function on the canvas as a box plot
 */
function renderKernelDFBoxMinMaxWhiskers(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  df: TpKernelDFData,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
) {
  const { mapx, mapy, pixelConv } = renderContext;
  const ctx = renderContext.canvasContext;
  // show box + min-max whiskers, without density
  ctx.fillStyle = fillColor.toCanvasString();
  ctx.strokeStyle = outlineColor.toCanvasString();
  ctx.beginPath();
  ctx.rect(
    mapx(centerX - 0.4 * sizeX),
    mapy(df.q25),
    mapx(centerX + 0.4 * sizeX) - mapx(centerX - 0.4 * sizeX),
    mapy(df.q75) - mapy(df.q25),
  );
  ctx.moveTo(mapx(centerX - 0.4 * sizeX), mapy(df.q50));
  ctx.lineTo(mapx(centerX + 0.4 * sizeX), mapy(df.q50));
  ctx.moveTo(mapx(centerX), mapy(df.q25));
  ctx.lineTo(mapx(centerX), mapy(df.minValue));
  ctx.moveTo(mapx(centerX - 0.2 * sizeX), mapy(df.minValue));
  ctx.lineTo(mapx(centerX + 0.2 * sizeX), mapy(df.minValue));
  ctx.moveTo(mapx(centerX), mapy(df.q75));
  ctx.lineTo(mapx(centerX), mapy(df.maxValue));
  ctx.moveTo(mapx(centerX - 0.2 * sizeX), mapy(df.maxValue));
  ctx.lineTo(mapx(centerX + 0.2 * sizeX), mapy(df.maxValue));
  ctx.fill();
  ctx.stroke();
}

/**
 * Renders a kernel as a combination of whiskers from a central point.
 * @param renderContext render context containing the canvas context and mapping functions
 * @param outlineColor color of the outline of the point and whiskers
 * @param fillColor color of the fill of the point
 * @param centerX center X coordinate of the point in data space
 * @param sizeX size of the point in X direction in data space (used for scaling the point and whiskers)
 * @param centerValue the value of the center of the kernel (e.g. mean or median)
 * @param lowerWhiskerValue the value of the lower whisker (e.g. min or 25th percentile)
 * @param higherWhiskerValue the value of the higher whisker (e.g. max or 75th percentile)
 */
export function renderKernelDFSimpleWhiskers(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
  centerValue: number,
  lowerWhiskerValue: number,
  higherWhiskerValue: number,
) {
  const { mapx, mapy, pixelConv } = renderContext;
  const ctx = renderContext.canvasContext;

  if (!isNaN(centerValue)) {
    ctx.fillStyle = fillColor.toCanvasString();
    ctx.strokeStyle = outlineColor.toCanvasString();
    const scr_x = mapx(centerX);
    const scr_y = mapy(centerValue);
    const radius = pixelConv(sizeX);

    // Draw min and max as a vertical line whiskers.
    for (const whiskerValue of [lowerWhiskerValue, higherWhiskerValue]) {
      if (!isNaN(whiskerValue)) {
        ctx.beginPath();
        ctx.moveTo(scr_x, scr_y);
        ctx.lineTo(scr_x, mapy(whiskerValue));
        ctx.moveTo(scr_x - radius, mapy(whiskerValue));
        ctx.lineTo(scr_x + radius, mapy(whiskerValue));
        ctx.fill();
        ctx.stroke();
      }
    }
  }
}

/**
 * Renders a kernel as a simple point
 * @param renderContext render context containing the canvas context and mapping functions
 * @param outlineColor color of the outline of the point
 * @param fillColor color of the fill of the point
 * @param centerX center X coordinate of the point in data space
 * @param sizeX size of the point in X direction in data space (used for scaling the point)
 * @param centerValue the value of the center of the kernel (e.g. mean or median)
 */
export function renderKernelDFSimpleCenter(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
  centerValue: number,
) {
  const { mapx, mapy, pixelConv } = renderContext;
  const ctx = renderContext.canvasContext;

  if (!isNaN(centerValue)) {
    ctx.fillStyle = fillColor.toCanvasString();
    ctx.strokeStyle = outlineColor.toCanvasString();
    const scr_x = mapx(centerX);
    const scr_y = mapy(centerValue);
    const radius = pixelConv(sizeX);
    ctx.beginPath();
    ctx.arc(scr_x, scr_y, radius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * Renders a kernel as a simple point with whiskers.
 * @param renderContext render context containing the canvas context and mapping functions
 * @param outlineColor color of the outline of the point and whiskers
 * @param fillColor color of the fill of the point
 * @param centerX center X coordinate of the point in data space
 * @param sizeX size of the point in X direction in data space (used for scaling the point and whiskers)
 * @param centerValue the value of the center of the kernel (e.g. mean or median)
 * @param lowerWhiskerValue the value of the lower whisker (e.g. min or 25th percentile)
 * @param higherWhiskerValue the value of the higher whisker (e.g. max or 75th percentile)
 */
export function renderKernelDFCenterAndWhiskers(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
  centerValue: number,
  lowerWhiskerValue: number,
  higherWhiskerValue: number,
) {
  renderKernelDFSimpleWhiskers(
    renderContext,
    outlineColor,
    fillColor,
    centerX,
    sizeX,
    centerValue,
    lowerWhiskerValue,
    higherWhiskerValue,
  );
  renderKernelDFSimpleCenter(renderContext, outlineColor, fillColor, centerX, sizeX, centerValue);
}

export function renderKernelDF(
  renderContext: TpAreaRenderContext | TpRenderKernelContext,
  df: TpKernelDFData,
  maxDensity: number,
  outlineColor: Color,
  fillColor: Color,
  centerX: number,
  sizeX: number,
  displayType: string,
  displayDensityMean: boolean,
) {
  if (!df.hasData) return;
  const { mapx, mapy, pixelConv } = renderContext;
  const ctx = renderContext.canvasContext;

  switch (displayType) {
    case KERNEL_DISPLAY_TYPE.DENSITY:
      renderKernelDFDensity(renderContext, df, maxDensity, outlineColor, fillColor, centerX, sizeX);
      break;
    case KERNEL_DISPLAY_TYPE.DENSITY_BOX:
      renderKernelDFDensityBox(renderContext, df, maxDensity, outlineColor, fillColor, centerX, sizeX);
      break;
    case KERNEL_DISPLAY_TYPE.BOX_MIN_MAX_WHISKERS:
      renderKernelDFBoxMinMaxWhiskers(renderContext, df, outlineColor, fillColor, centerX, sizeX);
      break;
    case KERNEL_DISPLAY_TYPE.MEAN:
      renderKernelDFSimpleCenter(renderContext, outlineColor, fillColor, centerX, sizeX, df.meanValue);
      break;
    case KERNEL_DISPLAY_TYPE.MEAN_MIN_MAX_WHISKERS:
      renderKernelDFCenterAndWhiskers(
        renderContext,
        outlineColor,
        fillColor,
        centerX,
        sizeX,
        df.meanValue,
        df.minValue,
        df.maxValue,
      );
      break;
  }

  // Draw a marker for the mean value as a cross
  if (displayDensityMean) {
    const meanX = mapx(centerX);
    const meanY = mapy(df.meanValue);
    const markerSize = pixelConv(5);
    drawMeanMarker(ctx, meanX, meanY, markerSize, outlineColor.toCanvasString(), pixelConv(2));
  }
}

export function getKernelDFCenterY(df: TpKernelDFData, displayType: string): number {
  switch (displayType) {
    case KERNEL_DISPLAY_TYPE.DENSITY:
    case KERNEL_DISPLAY_TYPE.DENSITY_BOX:
    case KERNEL_DISPLAY_TYPE.BOX_MIN_MAX_WHISKERS:
      return df.q50; // for density-based displays, we use the median as the center Y value
    case KERNEL_DISPLAY_TYPE.MEAN:
    case KERNEL_DISPLAY_TYPE.MEAN_MIN_MAX_WHISKERS:
      return df.meanValue;
    default:
      return df.meanValue;
  }
}
